// Shared definitions for follow-up activity and marketing / data-quality tracking.
// Stored on the contact record as `activities` (array), `nextFollowUpAt` (ISO date) and `dataQuality` (object).

export const ACTIVITY_TYPES = [
  { value: 'note', label: 'Note', color: '#64748b' },
  { value: 'call', label: 'Call', color: '#2a78d6' },
  { value: 'email', label: 'Email', color: '#6250d6' },
  { value: 'whatsapp', label: 'WhatsApp', color: '#1baf7a' },
  { value: 'meeting', label: 'Meeting', color: '#eb6834' },
];

export const ACTIVITY_OUTCOMES = [
  { value: 'positive', label: 'Positive', color: '#10b981' },
  { value: 'neutral', label: 'Neutral', color: '#64748b' },
  { value: 'no_response', label: 'No response', color: '#f59e0b' },
  { value: 'negative', label: 'Not interested', color: '#ef4444' },
  { value: 'unprofessional', label: 'Unprofessional', color: '#b91c1c' },
];

export const findOption = (options, value) => options.find((o) => o.value === value);

// ---- Data quality / marketing ----

export const QUALITY_FIELDS = [
  {
    key: 'emailStatus',
    label: 'Email',
    channel: 'email',
    options: [
      { value: 'unverified', label: 'Not verified', tone: 'muted' },
      { value: 'replied', label: 'Valid – replied', tone: 'good' },
      { value: 'no_reply', label: 'Valid – no reply', tone: 'warn' },
      { value: 'bounced', label: 'Bounced / invalid', tone: 'bad' },
      { value: 'wrong_person', label: 'Wrong person', tone: 'bad' },
      { value: 'unsubscribed', label: 'Unsubscribed', tone: 'bad' },
    ],
  },
  {
    key: 'phoneStatus',
    label: 'Phone',
    channel: 'phone',
    options: [
      { value: 'unverified', label: 'Not verified', tone: 'muted' },
      { value: 'reachable', label: 'Reachable', tone: 'good' },
      { value: 'not_answering', label: 'Not answering', tone: 'warn' },
      { value: 'switched_off', label: 'Switched off', tone: 'warn' },
      { value: 'wrong_number', label: 'Wrong number', tone: 'bad' },
      { value: 'dnd', label: 'DND / do not call', tone: 'bad' },
    ],
  },
  {
    key: 'whatsappStatus',
    label: 'WhatsApp',
    channel: 'whatsapp',
    options: [
      { value: 'unverified', label: 'Not verified', tone: 'muted' },
      { value: 'active', label: 'Active – responds', tone: 'good' },
      { value: 'seen_no_reply', label: 'Seen, no reply', tone: 'warn' },
      { value: 'not_on_whatsapp', label: 'Not on WhatsApp', tone: 'bad' },
      { value: 'blocked', label: 'Blocked us', tone: 'bad' },
    ],
  },
  {
    key: 'websiteStatus',
    label: 'Website',
    channel: 'website',
    options: [
      { value: 'unverified', label: 'Not checked', tone: 'muted' },
      { value: 'working', label: 'Working', tone: 'good' },
      { value: 'outdated', label: 'Outdated', tone: 'warn' },
      { value: 'under_construction', label: 'Under construction', tone: 'warn' },
      { value: 'down', label: 'Down / not found', tone: 'bad' },
    ],
  },
];

export const RESPONSIVENESS_OPTIONS = [
  { value: 'quick', label: 'Quick to respond', tone: 'good' },
  { value: 'slow', label: 'Slow to respond', tone: 'warn' },
  { value: 'none', label: 'Never responds', tone: 'bad' },
];

export const PROFESSIONALISM_OPTIONS = [
  { value: 'professional', label: 'Professional', tone: 'good' },
  { value: 'average', label: 'Average', tone: 'muted' },
  { value: 'unprofessional', label: 'Unprofessional', tone: 'bad' },
];

export const LEAD_TEMPERATURE_OPTIONS = [
  { value: 'hot', label: 'Hot', tone: 'bad', color: '#ef4444' },
  { value: 'warm', label: 'Warm', tone: 'warn', color: '#f59e0b' },
  { value: 'cold', label: 'Cold', tone: 'muted', color: '#3b82f6' },
];

export const CONSENT_OPTIONS = [
  { value: 'not_asked', label: 'Not asked', tone: 'muted' },
  { value: 'opted_in', label: 'Opted in', tone: 'good' },
  { value: 'opted_out', label: 'Opted out', tone: 'bad' },
];

export const RED_FLAGS = [
  { value: 'do_not_contact', label: 'Do not contact' },
  { value: 'fake_or_spam', label: 'Fake / spam data' },
  { value: 'duplicate', label: 'Duplicate contact' },
  { value: 'competitor', label: 'Competitor' },
  { value: 'rude', label: 'Rude / abusive' },
];

export const PREFERRED_CHANNEL_OPTIONS = ['Email', 'Phone', 'WhatsApp', 'In person', 'LinkedIn'];

export const BEST_TIME_OPTIONS = ['Morning (9–12)', 'Afternoon (12–4)', 'Evening (4–7)', 'Weekends', 'Anytime'];

export const EMPTY_QUALITY = {
  emailStatus: 'unverified',
  phoneStatus: 'unverified',
  whatsappStatus: 'unverified',
  websiteStatus: 'unverified',
  responsiveness: '',
  professionalism: '',
  leadTemperature: '',
  marketingConsent: 'not_asked',
  preferredChannel: '',
  bestTimeToContact: '',
  interests: '',
  rating: 0,
  redFlags: [],
  reviewedBy: '',
  reviewedAt: '',
};

export const getQuality = (contact) => ({ ...EMPTY_QUALITY, ...(contact?.dataQuality || {}) });

export const TONE_COLORS = {
  good: '#10b981',
  warn: '#f59e0b',
  bad: '#ef4444',
  muted: '#94a3b8',
};

// ---- Marketing readiness (derived) ----

export const READINESS = {
  ready: { value: 'ready', label: 'Ready for marketing', short: 'Ready', color: '#10b981' },
  verify: { value: 'verify', label: 'Needs verification', short: 'Verify', color: '#f59e0b' },
  blocked: { value: 'blocked', label: 'Do not contact', short: 'Do not contact', color: '#ef4444' },
};

const BAD_CHANNEL = {
  emailStatus: ['bounced', 'wrong_person', 'unsubscribed'],
  phoneStatus: ['wrong_number', 'dnd'],
  whatsappStatus: ['not_on_whatsapp', 'blocked'],
};
const GOOD_CHANNEL = {
  emailStatus: ['replied', 'no_reply'],
  phoneStatus: ['reachable', 'not_answering', 'switched_off'],
  whatsappStatus: ['active', 'seen_no_reply'],
};

// Blocked if they opted out or are flagged; ready if at least one channel is confirmed usable
export const getReadiness = (contact) => {
  const q = getQuality(contact);
  if (q.marketingConsent === 'opted_out') return READINESS.blocked;
  if (q.redFlags.some((flag) => ['do_not_contact', 'fake_or_spam', 'competitor'].includes(flag))) return READINESS.blocked;
  const usable = Object.keys(GOOD_CHANNEL).some(
    (key) => GOOD_CHANNEL[key].includes(q[key]) && !BAD_CHANNEL[key].includes(q[key])
  );
  return usable ? READINESS.ready : READINESS.verify;
};

// ---- Follow-ups ----

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const getFollowUpState = (iso) => {
  if (!iso) return null;
  const due = startOfDay(new Date(iso));
  if (isNaN(due)) return null;
  const today = startOfDay(new Date());
  const diffDays = Math.round((due - today) / 86400000);
  if (diffDays < 0) return { state: 'overdue', label: `Overdue · ${formatShortDate(iso)}`, color: '#ef4444', diffDays };
  if (diffDays === 0) return { state: 'today', label: 'Today', color: '#f59e0b', diffDays };
  if (diffDays === 1) return { state: 'soon', label: 'Tomorrow', color: '#3b82f6', diffDays };
  return { state: 'upcoming', label: formatShortDate(iso), color: '#64748b', diffDays };
};

export const formatShortDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: new Date(iso).getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });

export const getLastActivity = (contact) => {
  const list = Array.isArray(contact?.activities) ? contact.activities : [];
  return list.reduce((latest, a) => (!latest || new Date(a.createdAt) > new Date(latest.createdAt) ? a : latest), null);
};

export const newActivityId = () => `act_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
